import * as React from 'react';
import * as LucideIcons from 'lucide-react';
import { cn } from '@/lib/utils';

type LucideIconComponent = React.ComponentType<{ className?: string; size?: number; style?: React.CSSProperties }>;

function getIcon(name: string): LucideIconComponent | null {
  // Direct lookup by PascalCase Lucide name
  const directIcon = LucideIcons[name as keyof typeof LucideIcons];
  if (directIcon) return directIcon as LucideIconComponent;

  // Fallback: try converting snake_case/camelCase to PascalCase
  const pascalName = name
    .split(/[_\s-]+/)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join('');
  const convertedIcon = LucideIcons[pascalName as keyof typeof LucideIcons];
  if (convertedIcon) return convertedIcon as LucideIconComponent;

  return null;
}

interface LucideIconProps {
  name: string;
  size?: 'small' | 'medium' | 'large' | number;
  className?: string;
  style?: React.CSSProperties;
  color?: string;
}

export const LucideIcon: React.FC<LucideIconProps> = ({ name, size = 'medium', className, style, color }) => {
  const IconComponent = getIcon(name);

  if (!IconComponent) {
    return <span className={className} style={style}>?</span>;
  }

  const sizeValue = typeof size === 'number' ? size : { small: 16, medium: 20, large: 24 }[size];

  return (
    <IconComponent
      size={sizeValue}
      className={cn(className)}
      style={{ ...style, color }}
    />
  );
};
